/**
 * Test assignment manifest — wire real test users (by email) into seeded
 * students, schedules, and aide assignments without overwriting user profiles.
 *
 * Used by seed/generate_showcase_pack.js (offline) and Dev Seed Manager import.
 */

export const TEACHING_ROLES = ['teacher', 'teacher_full', 'case_manager', 'sped_chair']

/** @typedef {{ email: string, periods?: string[], studentShare?: number, directStudents?: number }} AssignmentEntry */

/**
 * @typedef {{
 *   version?: string,
 *   description?: string,
 *   schools?: Record<string, {
 *     caseManagers?: AssignmentEntry[],
 *     teachers?: AssignmentEntry[],
 *     paraeducators?: AssignmentEntry[]
 *   }>
 * }} TestAssignmentsManifest
 */

export const EMPTY_MANIFEST = { version: '1', schools: {} }

export function normalizeEmail(email) {
  return String(email || '')
    .trim()
    .toLowerCase()
}

/** @param {Array<{ id?: string, email?: string }>} users */
export function indexUsersByEmail(users = []) {
  /** @type {Map<string, object>} */
  const map = new Map()
  for (const user of users) {
    const key = normalizeEmail(user.email)
    if (key) map.set(key, user)
  }
  return map
}

/** @param {Map<string, object>} byEmail @param {string} email */
export function resolveUserByEmail(byEmail, email) {
  if (!email) return null
  return byEmail.get(normalizeEmail(email)) || null
}

/**
 * @param {TestAssignmentsManifest|null|undefined} manifest
 * @param {string} schoolId
 */
export function getSchoolAssignmentConfig(manifest, schoolId) {
  return manifest?.schools?.[schoolId] || {}
}

/**
 * Build assignment pools for one school. Manifest emails take priority; demo
 * users fill gaps by role.
 *
 * @param {TestAssignmentsManifest|null|undefined} manifest
 * @param {string} schoolId
 * @param {object[]} liveUsers — all users (real + demo) keyed by Auth UID
 * @param {object[]} [demoUsersByRole] — fallback pool when manifest entry missing
 */
export function buildSchoolAssignmentPools(
  manifest,
  schoolId,
  liveUsers,
  demoUsersByRole = liveUsers
) {
  const config = getSchoolAssignmentConfig(manifest, schoolId)
  const byEmail = indexUsersByEmail(liveUsers)

  const resolveList = (entries = [], fallbackRoles = []) => {
    const fromManifest = entries.map((e) => resolveUserByEmail(byEmail, e.email)).filter(Boolean)
    if (fromManifest.length) return fromManifest
    return demoUsersByRole.filter((u) => fallbackRoles.includes(u.role))
  }

  const caseManagers = resolveList(config.caseManagers, ['case_manager'])
  const teachers = resolveList(config.teachers, TEACHING_ROLES)
  const paraeducators = resolveList(config.paraeducators, ['paraeducator'])

  return { config, caseManagers, teachers, paraeducators, byEmail }
}

/**
 * Pick period teachers for one student using manifest period lists when present.
 *
 * When an entry has `studentShare`, only that fraction of students get the
 * manifest teacher — and only on one period from `periods` (rotated by
 * studentIndex so class view spreads across periods). Other periods stay on
 * the demo teacher pool.
 *
 * Entries without studentShare keep the old behavior: every student gets that
 * teacher on every listed period.
 *
 * @param {AssignmentEntry[]} teacherEntries
 * @param {object[]} teacherPool
 * @param {Map<string, object>} byEmail
 * @param {number} studentIndex
 * @param {number} [studentCount]
 */
export function buildTeachersByPeriod(
  teacherEntries,
  teacherPool,
  byEmail,
  studentIndex,
  studentCount = 0
) {
  /** @type {Record<string, string>} */
  const periods = {}

  // Shared-share teachers: carve contiguous index ranges (same pattern as CM).
  const shareEntries = (teacherEntries || []).filter(
    (e) => typeof e.studentShare === 'number' && e.studentShare > 0
  )
  const fullEntries = (teacherEntries || []).filter(
    (e) => !(typeof e.studentShare === 'number' && e.studentShare > 0)
  )

  let shareAssigned = 0
  const shareTargets = shareEntries.map((entry) => {
    const user = resolveUserByEmail(byEmail, entry.email)
    const share = Math.min(1, Math.max(0, Number(entry.studentShare) || 0))
    const count = user && studentCount > 0 ? Math.floor(studentCount * share) : 0
    const start = shareAssigned
    shareAssigned += count
    const periodList = entry.periods?.length ? entry.periods.map(String) : ['1']
    return { user, start, end: start + count, periodList }
  })

  const shareHit = shareTargets.find(
    (t) => studentIndex >= t.start && studentIndex < t.end && t.user
  )
  if (shareHit?.user?.id) {
    // Rotate among listed periods so students land in different class-view columns.
    const periodKey = shareHit.periodList[studentIndex % shareHit.periodList.length]
    periods[periodKey] = shareHit.user.id
  }

  for (let p = 1; p <= 7; p++) {
    const key = String(p)
    if (periods[key]) continue

    let teacherId = ''

    for (const entry of fullEntries) {
      const periodsList = entry.periods?.length ? entry.periods.map(String) : null
      if (periodsList && !periodsList.includes(key)) continue
      const user = resolveUserByEmail(byEmail, entry.email)
      if (user?.id) {
        teacherId = user.id
        break
      }
    }

    if (!teacherId && teacherPool.length) {
      // Prefer non-manifest-share teachers so Ryan doesn't fill every leftover period.
      const reservedIds = new Set(shareTargets.map((t) => t.user?.id).filter(Boolean))
      const pool = teacherPool.filter((u) => u.id && !reservedIds.has(u.id))
      const usePool = pool.length ? pool : teacherPool
      teacherId = usePool[(studentIndex + p) % usePool.length].id
    }

    if (teacherId) periods[key] = teacherId
  }

  return periods
}

/**
 * Apply manifest-driven assignments to in-memory student records for one school.
 *
 * @param {object[]} students — mutates app.studentData / schedule in place
 * @param {TestAssignmentsManifest|null|undefined} manifest
 * @param {string} schoolId
 * @param {object[]} liveUsers
 * @param {object[]} [demoUsers]
 */
export function applySchoolTestAssignments(
  students,
  manifest,
  schoolId,
  liveUsers,
  demoUsers = liveUsers
) {
  const { config, caseManagers, teachers, paraeducators, byEmail } = buildSchoolAssignmentPools(
    manifest,
    schoolId,
    liveUsers,
    demoUsers
  )

  const hasManifest =
    (config.caseManagers?.length || 0) +
      (config.teachers?.length || 0) +
      (config.paraeducators?.length || 0) >
    0

  // Schools omitted from the manifest keep generator defaults — do not fall
  // back to "all case_managers / teachers" (that would re-wire workspace demo
  // emails into every school when the manifest is intentionally one-school).
  if (!hasManifest) {
    return { updated: 0, schoolId }
  }

  if (!caseManagers.length && !teachers.length) {
    return { updated: 0, schoolId }
  }

  // When teachers use studentShare, leftover periods must come from other staff —
  // not the manifest-only pool (which would be just Ryan and re-collapse schedules).
  const teacherEntries = config.teachers || []
  const teachersUseShare = teacherEntries.some(
    (e) => typeof e.studentShare === 'number' && e.studentShare > 0
  )
  const manifestTeacherEmails = new Set(
    teacherEntries.map((e) => normalizeEmail(e.email)).filter(Boolean)
  )
  const demoTeacherPool = teachersUseShare
    ? liveUsers.filter(
        (u) =>
          TEACHING_ROLES.includes(u.role) &&
          u.id &&
          !manifestTeacherEmails.has(normalizeEmail(u.email)) &&
          (u.schoolId === schoolId ||
            (Array.isArray(u.schoolIds) && u.schoolIds.includes(schoolId)) ||
            !u.schoolId)
      )
    : teachers
  const periodTeacherPool = demoTeacherPool.length ? demoTeacherPool : teachers

  let updated = 0
  const cmEntries = config.caseManagers || []
  const hasShares = cmEntries.some((e) => typeof e.studentShare === 'number' && e.studentShare > 0)

  // When studentShare is set, carve a fraction of the school for each manifest CM
  // (by email order). Remaining students go to other case_manager users — not back
  // onto the manifest CM (otherwise share is a no-op). Prefer same-school CMs so
  // multi-school packs don't get cross-school caseloads.
  const manifestCmEmails = new Set(cmEntries.map((e) => normalizeEmail(e.email)).filter(Boolean))
  const nonManifestCms = liveUsers.filter(
    (u) => u.role === 'case_manager' && u.id && !manifestCmEmails.has(normalizeEmail(u.email))
  )
  const sameSchoolCms = nonManifestCms.filter(
    (u) => u.schoolId === schoolId || (Array.isArray(u.schoolIds) && u.schoolIds.includes(schoolId))
  )
  const fallbackCms = sameSchoolCms.length ? sameSchoolCms : nonManifestCms

  let shareAssigned = 0
  const shareTargets = hasShares
    ? cmEntries.map((entry) => {
        const user = resolveUserByEmail(byEmail, entry.email)
        const share = Math.min(1, Math.max(0, Number(entry.studentShare) || 0))
        const count = user ? Math.floor(students.length * share) : 0
        const start = shareAssigned
        shareAssigned += count
        return { user, start, end: start + count }
      })
    : []

  students.forEach((student, index) => {
    const app = student.app || (student.app = {})
    const sd = app.studentData || (app.studentData = {})

    if (hasShares && shareTargets.length) {
      const slot = shareTargets.find((t) => index >= t.start && index < t.end && t.user)
      if (slot?.user?.id) {
        sd.caseManagerId = slot.user.id
      } else {
        const pool = fallbackCms.length ? fallbackCms : caseManagers
        if (pool.length) sd.caseManagerId = pool[index % pool.length].id
      }
    } else if (caseManagers.length) {
      sd.caseManagerId = caseManagers[index % caseManagers.length].id
    }

    const teachersByPeriod = buildTeachersByPeriod(
      teacherEntries,
      periodTeacherPool,
      byEmail,
      index,
      students.length
    )

    if (Object.keys(teachersByPeriod).length) {
      app.schedule = app.schedule || {}
      // Share-mode: replace periods entirely so leftover slots aren't stuck on
      // a previous all-Ryan bake. Full-mode still merges (legacy).
      app.schedule.periods = teachersUseShare
        ? teachersByPeriod
        : { ...(app.schedule.periods || {}), ...teachersByPeriod }
    }

    app.staffIds = computeStaffIds(app)
    updated += 1
  })

  return { updated, schoolId, paraeducators, caseManagers, teachers }
}

/**
 * @param {object} app
 */
export function computeStaffIds(app) {
  const ids = new Set()
  const cm = app?.studentData?.caseManagerId
  if (cm) ids.add(cm)
  for (const id of app?.studentData?.districtSupportIds || []) {
    if (id) ids.add(id)
  }
  const evalCm = app?.evalCaseManagerId
  if (evalCm) ids.add(evalCm)
  for (const val of Object.values(app?.schedule?.periods || {})) {
    if (typeof val === 'string' && val) ids.add(val)
    else if (val && typeof val === 'object') {
      if (val.teacherId) ids.add(val.teacherId)
      if (val.coTeaching?.caseManagerId) ids.add(val.coTeaching.caseManagerId)
    }
  }
  for (const pid of Object.values(app?.providers || {})) {
    if (pid) ids.add(pid)
  }
  return [...ids]
}

/**
 * Rewire all students grouped by schoolId using the manifest + live user list.
 *
 * @param {object[]} students
 * @param {TestAssignmentsManifest|null|undefined} manifest
 * @param {object[]} liveUsers
 */
export function applyTestAssignmentsToStudents(students, manifest, liveUsers) {
  if (!manifest?.schools || !Object.keys(manifest.schools).length) {
    return { totalUpdated: 0, bySchool: {} }
  }

  const bySchool = {}
  /** @type {Record<string, object[]>} */
  const grouped = {}
  for (const student of students) {
    const sid = student.schoolId || 'unknown'
    if (!grouped[sid]) grouped[sid] = []
    grouped[sid].push(student)
  }

  let totalUpdated = 0
  for (const [schoolId, schoolStudents] of Object.entries(grouped)) {
    const result = applySchoolTestAssignments(schoolStudents, manifest, schoolId, liveUsers)
    bySchool[schoolId] = result
    totalUpdated += result.updated
  }

  // One-school manifests: strip workspace/demo emails from schools that were
  // intentionally omitted so generator defaults don't leave them as CM/teacher.
  const cleared = clearManifestEmailsFromOtherSchools(students, manifest, liveUsers)
  if (cleared) totalUpdated += cleared

  return { totalUpdated, bySchool }
}

/**
 * Collect every email listed anywhere in the manifest.
 * @param {TestAssignmentsManifest} manifest
 */
function collectManifestEmails(manifest) {
  const emails = new Set()
  for (const config of Object.values(manifest?.schools || {})) {
    for (const list of [config.caseManagers, config.teachers, config.paraeducators]) {
      for (const entry of list || []) {
        const e = normalizeEmail(entry.email)
        if (e) emails.add(e)
      }
    }
  }
  return emails
}

/**
 * For schools not listed in the manifest, replace any CM / period teacher whose
 * email appears in the manifest with other staff (so a middle-only Ryan wiring
 * does not leave him on elem/high from the generator's role pools).
 *
 * @param {object[]} students
 * @param {TestAssignmentsManifest} manifest
 * @param {object[]} liveUsers
 */
export function clearManifestEmailsFromOtherSchools(students, manifest, liveUsers) {
  const manifestSchoolIds = new Set(Object.keys(manifest?.schools || {}))
  const reservedEmails = collectManifestEmails(manifest)
  if (!manifestSchoolIds.size || !reservedEmails.size) return 0

  const reservedIds = new Set(
    liveUsers.filter((u) => reservedEmails.has(normalizeEmail(u.email))).map((u) => u.id)
  )
  if (!reservedIds.size) return 0

  const otherCms = liveUsers.filter(
    (u) => u.role === 'case_manager' && u.id && !reservedIds.has(u.id)
  )
  const otherTeachers = liveUsers.filter(
    (u) => TEACHING_ROLES.includes(u.role) && u.id && !reservedIds.has(u.id)
  )

  let updated = 0
  students.forEach((student, index) => {
    if (manifestSchoolIds.has(student.schoolId)) return

    const app = student.app || (student.app = {})
    const sd = app.studentData || (app.studentData = {})
    let changed = false

    if (reservedIds.has(sd.caseManagerId) && otherCms.length) {
      const schoolCms = otherCms.filter(
        (u) =>
          u.schoolId === student.schoolId ||
          (Array.isArray(u.schoolIds) && u.schoolIds.includes(student.schoolId))
      )
      const pool = schoolCms.length ? schoolCms : otherCms
      sd.caseManagerId = pool[index % pool.length].id
      changed = true
    }

    const periods = app.schedule?.periods
    if (periods && otherTeachers.length) {
      for (const [key, val] of Object.entries(periods)) {
        const tid = typeof val === 'string' ? val : val?.teacherId
        if (!reservedIds.has(tid)) continue
        const replacement = otherTeachers[(index + Number(key || 0)) % otherTeachers.length].id
        if (typeof val === 'string') {
          periods[key] = replacement
        } else if (val && typeof val === 'object') {
          periods[key] = { ...val, teacherId: replacement }
        }
        changed = true
      }
    }

    if (changed) {
      app.staffIds = computeStaffIds(app)
      updated += 1
    }
  })

  return updated
}

/**
 * Build a minimal aide schedule doc for a manifest para who has no pack schedule yet
 * (e.g. a real login account currently seeded as case_manager/teacher).
 *
 * @param {object} paraUser — { id, email, ... }
 * @param {string} schoolId
 * @param {string[]} directAssignment — student doc ids
 */
export function createMinimalAideSchedule(paraUser, schoolId, directAssignment) {
  return {
    id: paraUser.id,
    schoolId,
    classAssignment: {},
    directAssignment: [...directAssignment],
    studentIds: [...directAssignment],
    schedule: {},
    timeBlocks: {},
    weeklyOverrides: [],
    dateOverrides: {},
    scheduleExceptions: []
  }
}

/**
 * Rewire aide schedule direct assignments for manifest paraeducators.
 * Creates missing schedule docs when a real login email is listed as a para
 * but the pack has no aideSchedules/{uid} yet (workspace role rotator case).
 *
 * @param {object[]} aideSchedules — mutates in place; may push new docs
 * @param {object[]} students
 * @param {TestAssignmentsManifest|null|undefined} manifest
 * @param {object[]} liveUsers
 */
export function applyTestAssignmentsToAideSchedules(aideSchedules, students, manifest, liveUsers) {
  if (!manifest?.schools) return { updated: 0, created: 0 }

  const schedules = aideSchedules || []
  let updated = 0
  let created = 0
  const byEmail = indexUsersByEmail(liveUsers)

  for (const [schoolId, config] of Object.entries(manifest.schools)) {
    const entries = config?.paraeducators || []
    if (!entries.length) continue

    // Initials have no services yet, so they get no para assignment. Checked
    // inline rather than via planStatus.js: this module is also loaded by the
    // offline seed scripts, which run without the '@/' alias.
    const iepStudents = students.filter(
      (s) =>
        s.schoolId === schoolId &&
        s.app?.studentData?.plan === 'IEP' &&
        s.app?.studentData?.planStatus !== 'initial'
    )

    for (const entry of entries) {
      const paraUser = resolveUserByEmail(byEmail, entry.email)
      if (!paraUser?.id) continue

      const count = Math.min(entry.directStudents || 6, iepStudents.length)
      const directAssignment = iepStudents.slice(0, count).map((s) => s.id)

      let schedule = schedules.find((s) => s.id === paraUser.id && s.schoolId === schoolId)
      if (!schedule) {
        // One schedule doc per aide UID; if they already have a doc for another
        // school, still create/update the school-scoped assignment they need.
        schedule = schedules.find((s) => s.id === paraUser.id)
      }

      if (schedule) {
        schedule.schoolId = schedule.schoolId || schoolId
        schedule.directAssignment = directAssignment
        schedule.studentIds = directAssignment
        updated += 1
      } else {
        schedules.push(createMinimalAideSchedule(paraUser, schoolId, directAssignment))
        created += 1
      }
    }
  }

  return { updated, created }
}

/**
 * Parse manifest JSON safely.
 * @param {unknown} raw
 */
export function parseTestAssignmentsManifest(raw) {
  if (!raw || typeof raw !== 'object') return { ...EMPTY_MANIFEST }
  const manifest = /** @type {TestAssignmentsManifest} */ (raw)
  if (!manifest.schools || typeof manifest.schools !== 'object') {
    return { ...EMPTY_MANIFEST, version: manifest.version || '1' }
  }
  return manifest
}

/** Default manifest path for Node scripts. */
export const SEED_MANIFEST_PATHS = [
  'seed/test_assignments.json',
  'seed/test_assignments.json.example'
]

/** URL served from public/ for hosted dev import. */
export const PUBLIC_MANIFEST_URL = '/seed-demo/test_assignments.json'
